import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, FormLabel } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';

import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner/TherigyLoadingSpinner';
import FlexCenter from 'App/components/styles/FlexCenter';
import PESService from 'App/services/PESService';
import { errorHandler } from 'App/utils';
import { TextField } from 'Shared/form/TextField';

import MedispanMedicationTable from './MedispanMedicationTable';

const StyledH4 = styled.h4`
    margin-top: 64px;
    margin-bottom: 16px;
`;

const SectionSpacer = styled.div`
    margin-top: 24px;
`;

const FindMedispanMedicationForm = () => {
    const [isSearching, setIsSearching] = useState(false);
    const { values, setFieldValue } = useFormikContext();
    const previousMedicationNameRef = useRef(values.medicationName);

    const runSearch = (nextSearchParams = null) => {
        const medicationName = values.medicationName?.trim();

        setFieldValue('hasSearched', true);
        setIsSearching(true);

        let params = nextSearchParams;
        if (!nextSearchParams) {
            params = { ...values.searchParams, page: 0 };
            setFieldValue('searchParams.page', 0);
        }
        return PESService.searchMedispanData('name', medicationName, params)
            .then((res) => {
                setFieldValue('medispanMedicationData', res);
            })
            .finally(() => {
                setIsSearching(false);
            });
    };

    /* Reset hasSearched to false when medicationName changes to allow new search with updated
    medicationName without showing "not found" message from previous search */
    useEffect(() => {
        const previousMedicationName = previousMedicationNameRef.current;
        const hasMedicationNameChanged = previousMedicationName !== values.medicationName;
        if (hasMedicationNameChanged && values.hasSearched) {
            setFieldValue('hasSearched', false);
        }
        previousMedicationNameRef.current = values.medicationName;
    }, [setFieldValue, values.hasSearched, values.medicationName]);

    const handleSubmit = (event) => {
        // handle keyboard enter key press for search
        event.preventDefault();
        if (!isSearching && values.medicationName?.trim()) {
            runSearch();
        }
    };

    const handleSortChange = (sort) => {
        const nextSearchParams = {
            ...values.searchParams,
            sortKey: sort.sortKey,
            sortOrder: sort.sortOrder,
            page: 0,
        };
        setFieldValue('searchParams.sortKey', sort.sortKey);
        setFieldValue('searchParams.sortOrder', sort.sortOrder);
        setFieldValue('searchParams.page', 0);
        runSearch(nextSearchParams).catch(errorHandler);
    };

    const handlePageChange = (page) => {
        const nextSearchParams = {
            ...values.searchParams,
            count: page.pageSize,
            page: page.pageIndex - 1,
        };
        setFieldValue('searchParams.count', page.pageSize);
        setFieldValue('searchParams.page', page.pageIndex - 1);
        runSearch(nextSearchParams).catch(errorHandler);
    };

    return (
        <>
            <StyledH4>Find Medication from Medispan</StyledH4>
            <form className="add-medication-search" onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <FormLabel htmlFor="medicationName">
                            <strong>Medication Name</strong>
                        </FormLabel>
                        <TextField fieldName="medicationName" name="medicationName" placeholder="Medication Name" />
                    </Col>
                    <Col md={4}>
                        <div
                            className="form-button-group"
                            style={{ display: 'flex', justifyContent: 'flex-start', paddingTop: '15px' }}
                        >
                            <Button
                                variant="primary"
                                disabled={isSearching || !values.medicationName?.trim()}
                                onClick={() => runSearch()}
                                type="button"
                            >
                                Search
                            </Button>
                        </div>
                    </Col>
                </Row>
            </form>
            {!isSearching && values.medispanMedicationData.results?.length === 0 && values.hasSearched ? (
                <SectionSpacer className="text-danger">{values.medicationName} not found.</SectionSpacer>
            ) : null}
            <SectionSpacer>
                <Col md={8}>
                    {values.hasSearched && isSearching && values.medispanMedicationData?.results?.length === 0 ? (
                        <FlexCenter>
                            <TherigyLoadingSpinner />
                        </FlexCenter>
                    ) : null}
                    {values.medispanMedicationData?.results?.length > 0 ? (
                        <MedispanMedicationTable
                            data={values.medispanMedicationData.results}
                            totalRows={values.medispanMedicationData.total}
                            onPageChange={handlePageChange}
                            onSortChange={handleSortChange}
                            currentPage={values.searchParams.page + 1}
                            isSearching={isSearching}
                            id="find-medication-table"
                        />
                    ) : null}
                </Col>
            </SectionSpacer>
        </>
    );
};

export default FindMedispanMedicationForm;
